import { EMPTY_VIEW_ICONS, EmptyView } from '@shared/components/ui/EmptyView';
import { TotalResults } from '@shared/components/ui/TotalResults';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import {
  useDownloadWithdrawalPaymentFile,
  useWithdrawalPaymentFilesColumns,
  useWithdrawalPaymentFilesInfinite,
  useWithdrawalPaymentFilesMain,
} from '../../hooks';
import type { WithdrawalPaymentFile } from '../../models';
import { PaymentFileCardList, WithdrawalPaymentFilesAgGridTable, WithdrawalPaymentFilesBreadcrumb } from '../display';
import { ErrorState, LoadingState } from '../feedback';
import { PaymentFilesFiltersSection } from '../interaction';
import { ViewPaymentFileModal, ViewPaymentFileModalRef } from '../modals';

export const Main = () => {
  const { t } = useTranslation('withdrawalPaymentFilesModule');
  const viewModalRef = useRef<ViewPaymentFileModalRef>(null);
  const { downloadFile, downloadingFileId } = useDownloadWithdrawalPaymentFile();

  const { apiFilters, filtersForUi, handleFiltersChange, hasActiveFilters } = useWithdrawalPaymentFilesMain();
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const { data, isPending, isError, error, refetch, isFetching, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useWithdrawalPaymentFilesInfinite(apiFilters);

  const paymentFiles = useMemo(() => data?.pages.flatMap((page) => page.data) ?? [], [data]);
  const totalFromApi = data?.pages[0]?.meta.total;

  const handleViewDetail = useCallback((file: WithdrawalPaymentFile) => {
    viewModalRef.current?.open(file);
  }, []);

  const handleDownload = useCallback(
    (file: WithdrawalPaymentFile) => {
      void downloadFile(file.id);
    },
    [downloadFile],
  );

  const { columnDefs } = useWithdrawalPaymentFilesColumns({
    onViewDetail: handleViewDetail,
    onDownload: handleDownload,
    downloadingFileId,
  });

  const isRefetching = isFetching && !isPending && !isFetchingNextPage;

  const paymentFilesViewProps = useMemo(
    () => ({
      paymentFiles,
      fetchNextPage: () => {
        void fetchNextPage();
      },
      hasNextPage: hasNextPage ?? false,
      isFetchingNextPage,
      total: totalFromApi ?? paymentFiles.length,
      onViewDetail: handleViewDetail,
      onDownload: handleDownload,
      downloadingFileId,
    }),
    [
      paymentFiles,
      fetchNextPage,
      hasNextPage,
      isFetchingNextPage,
      totalFromApi,
      handleViewDetail,
      handleDownload,
      downloadingFileId,
    ],
  );

  const renderPaymentFiles = () => {
    if (isMobile) {
      return <PaymentFileCardList {...paymentFilesViewProps} showActions />;
    }

    return (
      <WithdrawalPaymentFilesAgGridTable
        paymentFiles={paymentFiles}
        columnDefs={columnDefs}
        fetchNextPage={paymentFilesViewProps.fetchNextPage}
        hasNextPage={paymentFilesViewProps.hasNextPage}
        isFetchingNextPage={paymentFilesViewProps.isFetchingNextPage}
      />
    );
  };

  useEffect(() => {
    if (!isPending && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [isPending, isInitialLoad]);

  // Spinner solo en la carga inicial (mismo patrón que Orders/List)
  if (isInitialLoad) {
    return <LoadingState />;
  }

  if (isError || !data) {
    return <ErrorState error={error ?? new Error(t('errors.generic'))} onRetry={refetch} />;
  }

  return (
    <>
      <WithdrawalPaymentFilesBreadcrumb />
      <div className={`flex min-h-0 flex-1 flex-col gap-4 pb-6 pt-4 ${isMobile ? 'px-4' : 'px-6'}`}>
        {/* Filtros - Filters section */}
        <div className="shrink-0 space-y-4">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <PaymentFilesFiltersSection filters={filtersForUi} onFiltersChange={handleFiltersChange} />
            <TotalResults total={totalFromApi ?? paymentFiles.length} />
          </div>
        </div>

        {/* Tabla - Payment files table */}
        <div
          className={`relative flex min-h-0 flex-1 flex-col overflow-hidden ${
            paymentFiles.length === 0 ? 'min-h-[min(420px,calc(100vh-20rem))]' : ''
          }`}
        >
          <div
            className={`min-h-0 flex-1 flex flex-col transition-opacity duration-300 ${
              isRefetching ? 'opacity-50 pointer-events-none' : 'opacity-100'
            }`}
          >
            {renderPaymentFiles()}
          </div>

          {paymentFiles.length === 0 && (
            <div className="absolute inset-0 z-[1] flex items-center justify-center bg-background/90">
              <EmptyView
                type={hasActiveFilters ? 'noResults' : 'emptyState'}
                icon={EMPTY_VIEW_ICONS.ORDERS}
                title={hasActiveFilters ? t('noResults') : t('emptyState')}
              />
            </div>
          )}
        </div>
      </div>

      <ViewPaymentFileModal ref={viewModalRef} />
    </>
  );
};
